import { Injectable, ElementRef } from '@angular/core';
import { getEmbedSDK, ILookerConnection, IEmbedBuilder } from '@looker/embed-sdk';
import { environment } from '../../../environments/environment';
import { BehaviorSubject, Observable } from 'rxjs';
import { getLookerDashboardEmbedUrl } from './looker-dashboards.config';

export interface LookerEmbedState {
  isLoading: boolean;
  errorMessage: string;
}

export interface LookerEmbedParams {
  host: string;
  dashboardId?: string;
  dashboardUrl?: string;
}

interface LookerCookielessSessionTokens {
  session_reference_token?: string;
  session_reference_token_ttl?: number;
  authentication_token?: string;
  authentication_token_ttl?: number;
  api_token?: string;
  api_token_ttl?: number;
  navigation_token?: string;
  navigation_token_ttl?: number;
}

interface LookerGenerateTokensRequest {
  api_token?: string;
  navigation_token?: string;
}

export interface LookerEmbedWarmOptions {
  dashboardRouteKey?: string;
}

@Injectable({
  providedIn: 'root',
})
export class LookerEmbedService {
  public readonly LOOKER_HOST = 'https://mobileinsight.cloud.looker.com';
  private readonly DEFAULT_DASHBOARD_ID = '11';
  private readonly DASHBOARD_CONNECT_TIMEOUT_MS = 90_000;

  private stateSubject = new BehaviorSubject<LookerEmbedState>({
    isLoading: true,
    errorMessage: '',
  });

  public state$: Observable<LookerEmbedState> = this.stateSubject.asObservable();

  private dashboardConnection?: ILookerConnection;
  private sessionReferenceToken?: string;
  private lastAcquireTokens?: LookerCookielessSessionTokens;
  private setupGeneration = 0;
  private cookielessOrgId?: string;
  private cookielessInitialized = false;
  private sessionWarmupPromise?: Promise<void>;
  private lastWarmupKey?: string;
  private connectTimeoutHandle?: number;
  private currentAcquireParams: LookerEmbedParams = {
    host: this.LOOKER_HOST,
    dashboardId: this.DEFAULT_DASHBOARD_ID,
  };

  get currentState(): LookerEmbedState {
    return this.stateSubject.value;
  }

  private updateState(state: Partial<LookerEmbedState>): void {
    window.setTimeout(() => {
      this.stateSubject.next({
        ...this.stateSubject.value,
        ...state,
      });
    }, 0);
  }

  public async setupLookerEmbed(
    embedContainer: ElementRef,
    organizationId: string,
    params: LookerEmbedParams = {
      host: this.LOOKER_HOST,
      dashboardId: this.DEFAULT_DASHBOARD_ID,
    }
  ): Promise<void> {
    if (!params.dashboardId && !params.dashboardUrl) {
      params.dashboardId = this.DEFAULT_DASHBOARD_ID;
    }

    const setupId = ++this.setupGeneration;
    this.currentAcquireParams = params;
    this.clearConnectTimeout();

    try {
      this.updateState({ isLoading: true, errorMessage: '' });

      const orgChanged = this.cookielessOrgId !== organizationId;
      if (orgChanged) {
        this.clearCookielessSession();
        this.cookielessOrgId = organizationId;
      }

      this.ensureCookielessInitialized(organizationId, params);

      // Prefer in-place navigation when the cookieless session/iframe already exists.
      if (!orgChanged && this.dashboardConnection && setupId === this.setupGeneration) {
        const navigated = await this.navigateExistingDashboard(params);
        if (navigated && setupId === this.setupGeneration) {
          this.updateState({ isLoading: false });
          return;
        }
      }

      if (embedContainer?.nativeElement) {
        embedContainer.nativeElement.innerHTML = '';
      }
      this.dashboardConnection = undefined;

      const embedSDK = getEmbedSDK();
      const embedBuilder = params.dashboardUrl
        ? embedSDK.createDashboardWithUrl(params.dashboardUrl)
        : embedSDK.createDashboardWithId(params.dashboardId!);

      embedBuilder.withClassName('looker-embed-dashboard');
      this.addEventsListeners(embedBuilder, setupId);

      if (!embedContainer?.nativeElement) {
        throw new Error('Embed container not available');
      }

      this.armConnectTimeout(setupId);

      const dashboard = embedBuilder
        .appendTo(embedContainer.nativeElement)
        .build();

      this.dashboardConnection = await dashboard.connect();

      if (setupId !== this.setupGeneration) {
        return;
      }

      this.clearConnectTimeout();
      // Hide the overlay once the iframe is connected. Tile queries can still run.
      this.updateState({ isLoading: false });
    } catch (error) {
      if (setupId !== this.setupGeneration) {
        return;
      }

      this.clearConnectTimeout();
      console.error('Error connecting to Looker dashboard:', error);
      this.updateState({
        errorMessage: error instanceof Error ? error.message : 'Failed to load dashboard',
        isLoading: false,
      });
      throw error;
    }
  }

  /** Tear down the iframe only; keep cookieless session for faster re-entry. */
  public teardownEmbed(embedContainer: ElementRef): void {
    this.setupGeneration++;
    this.clearConnectTimeout();

    if (embedContainer?.nativeElement) {
      embedContainer.nativeElement.innerHTML = '';
    }

    this.dashboardConnection = undefined;
  }

  /** Full reset when org changes or user logs out. */
  public cleanupDashboard(embedContainer: ElementRef): void {
    this.teardownEmbed(embedContainer);
    this.clearCookielessSession();
  }

  public clearCookielessSession(): void {
    this.cookielessInitialized = false;
    this.cookielessOrgId = undefined;
    this.sessionReferenceToken = undefined;
    this.lastAcquireTokens = undefined;
    this.sessionWarmupPromise = undefined;
    this.lastWarmupKey = undefined;
  }

  /**
   * Prime the Looker cookieless session so the first dashboard open does not
   * pay the full acquire-session latency.
   */
  public warmCookielessSession(
    organizationId: string,
    options?: LookerEmbedWarmOptions,
  ): void {
    if (!organizationId) {
      return;
    }

    const dashboardUrl = options?.dashboardRouteKey
      ? getLookerDashboardEmbedUrl(options.dashboardRouteKey)
      : undefined;

    getEmbedSDK();

    if (this.cookielessOrgId && this.cookielessOrgId !== organizationId) {
      this.clearCookielessSession();
    }

    this.ensureCookielessInitialized(organizationId, {
      host: this.LOOKER_HOST,
      dashboardId: this.DEFAULT_DASHBOARD_ID,
    });

    const warmupKey = `${organizationId}|${dashboardUrl ?? ''}`;
    if (this.sessionReferenceToken && this.lastWarmupKey === warmupKey) {
      return;
    }

    if (this.sessionWarmupPromise) {
      return;
    }

    this.sessionWarmupPromise = this.prefetchAcquireSession(organizationId, dashboardUrl)
      .then(() => {
        this.lastWarmupKey = warmupKey;
      })
      .finally(() => {
        this.sessionWarmupPromise = undefined;
      });
  }

  private async prefetchAcquireSession(
    organizationId: string,
    embedPath?: string,
  ): Promise<void> {
    const acquireSessionUrl = `${this.getApiBaseUrl()}/organizations/${organizationId}/looker/session/acquire`;
    const body: Record<string, string> = {
      embed_domain: window.location.origin,
    };

    if (embedPath) {
      body.embed_path = embedPath;
    }

    try {
      const response = await fetch(acquireSessionUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: this.getAuthorizationHeader(),
        },
        body: JSON.stringify(body),
      });

      if (!response.ok) {
        return;
      }

      const tokens = (await response.json()) as LookerCookielessSessionTokens;
      this.persistSessionReferenceToken(tokens);
    } catch (error) {
      console.warn('Looker session warmup failed', error);
    }
  }

  private ensureCookielessInitialized(
    organizationId: string,
    params: LookerEmbedParams
  ): void {
    if (this.cookielessInitialized && this.cookielessOrgId === organizationId) {
      return;
    }

    const embedSDK = getEmbedSDK();
    const apiBaseUrl = this.getApiBaseUrl();
    const acquireSessionUrl = `${apiBaseUrl}/organizations/${organizationId}/looker/session/acquire`;
    const generateTokensUrl = `${apiBaseUrl}/organizations/${organizationId}/looker/session/tokens`;
    const lookerHost = this.getLookerEmbedHost(params.host);

    embedSDK.initCookieless(
      lookerHost,
      this.createAcquireSessionCallback(acquireSessionUrl),
      this.createGenerateTokensCallback(generateTokensUrl)
    );

    this.cookielessInitialized = true;
    this.cookielessOrgId = organizationId;
  }

  private async navigateExistingDashboard(params: LookerEmbedParams): Promise<boolean> {
    try {
      const dashboardConnection = this.dashboardConnection?.asDashboardConnection?.() as {
        loadUrl?: (url: string) => Promise<void>;
        loadDashboard?: (id: string | number) => Promise<void>;
      } | undefined;

      if (!dashboardConnection) {
        return false;
      }

      if (params.dashboardUrl && dashboardConnection.loadUrl) {
        await dashboardConnection.loadUrl(params.dashboardUrl);
        return true;
      }

      if (params.dashboardId && dashboardConnection.loadDashboard) {
        await dashboardConnection.loadDashboard(params.dashboardId);
        return true;
      }

      return false;
    } catch (error) {
      console.warn('In-place Looker navigation failed; rebuilding embed', error);
      return false;
    }
  }

  private armConnectTimeout(setupId: number): void {
    this.connectTimeoutHandle = window.setTimeout(() => {
      if (setupId !== this.setupGeneration || !this.currentState.isLoading) {
        return;
      }

      console.error('Looker dashboard connect timed out');
      this.updateState({
        errorMessage: 'Dashboard is taking too long to load. Please try again.',
        isLoading: false,
      });
    }, this.DASHBOARD_CONNECT_TIMEOUT_MS);
  }

  private clearConnectTimeout(): void {
    if (this.connectTimeoutHandle) {
      window.clearTimeout(this.connectTimeoutHandle);
      this.connectTimeoutHandle = undefined;
    }
  }

  private getApiBaseUrl(): string {
    return environment.apiUrl.endsWith('/') ? environment.apiUrl.slice(0, -1) : environment.apiUrl;
  }

  private getLookerEmbedHost(host: string): string {
    return host.replace(/^https?:\/\//, '').replace(/\/$/, '');
  }

  private getAuthorizationHeader(): string {
    const token = localStorage.getItem('authToken');
    if (token) {
      return `Bearer ${token}`;
    }

    console.error('No authentication token found in localStorage');
    return '';
  }

  private persistSessionReferenceToken(tokens: LookerCookielessSessionTokens | undefined): void {
    if (!tokens) {
      return;
    }

    this.lastAcquireTokens = tokens;
    if (tokens.session_reference_token) {
      this.sessionReferenceToken = tokens.session_reference_token;
    }
  }

  private createAcquireSessionCallback(acquireSessionUrl: string) {
    return async (): Promise<LookerCookielessSessionTokens> => {
      // Prefer tokens already fetched by warmup so connect does not wait on a second Looker acquire.
      if (
        this.lastAcquireTokens?.authentication_token
        && this.lastAcquireTokens?.session_reference_token
      ) {
        const warmedTokens = this.lastAcquireTokens;
        this.lastAcquireTokens = undefined;
        return warmedTokens;
      }

      // If warmup is still in flight, wait for it instead of racing a duplicate acquire.
      if (this.sessionWarmupPromise) {
        await this.sessionWarmupPromise;
        if (
          this.lastAcquireTokens?.authentication_token
          && this.lastAcquireTokens?.session_reference_token
        ) {
          const warmedTokens = this.lastAcquireTokens;
          this.lastAcquireTokens = undefined;
          return warmedTokens;
        }
      }

      const params = this.currentAcquireParams;
      const body: Record<string, string> = {
        embed_domain: window.location.origin,
      };

      if (params.dashboardUrl) {
        body.embed_path = params.dashboardUrl;
      }

      if (this.sessionReferenceToken) {
        body.session_reference_token = this.sessionReferenceToken;
      }

      const response = await fetch(acquireSessionUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: this.getAuthorizationHeader(),
        },
        body: JSON.stringify(body),
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Looker acquire session failed (${response.status}): ${errorText}`);
      }

      const tokens = (await response.json()) as LookerCookielessSessionTokens;
      this.persistSessionReferenceToken(tokens);
      // Tokens are consumed by this acquire callback.
      this.lastAcquireTokens = undefined;
      return tokens;
    };
  }

  private createGenerateTokensCallback(generateTokensUrl: string) {
    return async ({
      api_token,
      navigation_token,
    }: LookerGenerateTokensRequest): Promise<LookerCookielessSessionTokens> => {
      const body: Record<string, string | undefined> = {
        api_token,
        navigation_token,
      };

      if (this.sessionReferenceToken) {
        body.session_reference_token = this.sessionReferenceToken;
      }

      const response = await fetch(generateTokensUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: this.getAuthorizationHeader(),
        },
        body: JSON.stringify(body),
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Looker generate tokens failed (${response.status}): ${errorText}`);
      }

      const tokens = (await response.json()) as LookerCookielessSessionTokens;
      this.persistSessionReferenceToken(tokens);

      if (tokens.session_reference_token_ttl === 0) {
        this.sessionReferenceToken = undefined;
      }

      return tokens;
    };
  }

  private addEventsListeners(embedBuilder: IEmbedBuilder, setupId: number): void {
    embedBuilder
      .on('dashboard:loaded', () => {
        if (setupId !== this.setupGeneration) {
          return;
        }
        this.clearConnectTimeout();
        this.updateState({ isLoading: false });
      })
      .on('dashboard:load_error', (event: any) => {
        if (setupId !== this.setupGeneration) {
          return;
        }
        this.clearConnectTimeout();
        console.error('Dashboard load error:', event);
        this.updateState({
          errorMessage: event?.message ?? 'Failed to load dashboard',
          isLoading: false,
        });
      })
      .on('embed:error', (event: any) => {
        if (setupId !== this.setupGeneration) {
          return;
        }
        this.clearConnectTimeout();
        console.error('Embed error:', event);
        this.updateState({
          errorMessage: 'Failed to embed dashboard. Check console for details.',
          isLoading: false,
        });
      })
      .on('acquire_session:error', (event: any) => {
        if (setupId !== this.setupGeneration) {
          return;
        }
        this.clearConnectTimeout();
        console.error('Acquire session error:', event);
        this.updateState({
          errorMessage: 'Failed to acquire Looker session. Check backend logs.',
          isLoading: false,
        });
      })
      .on('generate_tokens:error', (event: any) => {
        if (setupId !== this.setupGeneration) {
          return;
        }
        console.error('Generate tokens error:', event);
        this.updateState({
          errorMessage: 'Failed to refresh Looker tokens.',
          isLoading: false,
        });
      })
      .on('acquire_session:response', (response: LookerCookielessSessionTokens) => {
        this.persistSessionReferenceToken(response);
      })
      .on('session:status', (event: any) => {
        if (event?.session_reference_token_ttl === 0) {
          this.sessionReferenceToken = undefined;
        }
      });
  }
}
